// Smart tolerance parser. When the user types a single number into the
// symmetric tolerance field, we keep it symmetric: ±value. When the
// input contains two numbers (any common separator: minus sign / slash
// / space), we auto-switch the row to asymmetric — first number → upper
// deviation, second → lower (its sign is preserved). The prototype
// (handoff table.jsx commitSymmetric) calls this out as the main
// ergonomics win over a fixed mode toggle.
//
// Examples:
//   "0,3"        → { tolMode: 'symmetric', tol: 0.3 }
//   "0,3-0,2"    → { tolMode: 'asymmetric', tolUpper: 0.3, tolLower: -0.2 }
//   "+0,3 -0,2"  → { tolMode: 'asymmetric', tolUpper: 0.3, tolLower: -0.2 }
//   "0,3 / 0,2"  → { tolMode: 'asymmetric', tolUpper: 0.3, tolLower: 0.2 }
//   "-0,1 -0,2"  → { tolMode: 'asymmetric', tolUpper: -0.1, tolLower: -0.2 }
//                  (unidirectional below nominal — both negative is valid)
//
// Returns null when the input is empty or contains no number at all,
// so the caller can leave the row's stored values untouched.

const NUMBER_RE = /([+-]?\d+(?:[.,]\d+)?)/g;

function toNumber(s) {
  const n = parseFloat(String(s).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function smartParseTol(raw) {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (trimmed === '') return null;

  const matches = [...trimmed.matchAll(NUMBER_RE)].map((m) => m[1]);

  if (matches.length >= 2) {
    const u = toNumber(matches[0]);
    const l = toNumber(matches[1]);
    if (u === null || l === null) return null;
    // Round-trip: a +0.3 / -0.3 pair from the smart parser collapses
    // back to symmetric so the stored shape matches what the user sees
    // (single ± value).
    if (Math.abs(u + l) < 1e-9 && u > 0) {
      return { tolMode: 'symmetric', tol: u, tolUpper: 0, tolLower: 0 };
    }
    return { tolMode: 'asymmetric', tol: 0, tolUpper: u, tolLower: l };
  }

  if (matches.length === 1) {
    const t = toNumber(matches[0]);
    if (t === null) return null;
    return { tolMode: 'symmetric', tol: Math.abs(t), tolUpper: 0, tolLower: 0 };
  }

  return null;
}
